"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  initials: "JD",
  quote: "Omni Wealth Management has exceeded all expectations. Their discreet, professional approach to managing our estate operations is unparalleled.",
  name: "Jonathan Davis",
  company: "Private Investor"
}, {
  id: 2,
  initials: "SM",
  quote: "The level of personalized attention and meticulous care they provide gives us complete peace of mind. Highly recommend their services.",
  name: "Sarah Mitchell",
  company: "CEO, Tech Ventures"
}, {
  id: 3,
  initials: "RC",
  quote: "From concierge coordination to estate oversight, Omni Wealth Management handles everything seamlessly. Truly a premium experience.",
  name: "Robert Chen",
  company: "Real Estate Entrepreneur"
}, {
  id: 4,
  initials: "EW",
  quote: "Their confidential and sophisticated service is exactly what we needed for our Los Angeles properties. Exceptional professionalism.",
  name: "Elizabeth Warren",
  company: "Art Collector"
}, {
  id: 5,
  initials: "MK",
  quote: "Omni's attention to detail and commitment to excellence make them the gold standard in private property management.",
  name: "Michael Klein",
  company: "Investment Banker"
}];
export default function ClientTestimonials() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.15
  });
  const [api, setApi] = useState<CarouselApi>();
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!api) return;
    const startAutoScroll = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (!isPaused) {
          api.scrollNext();
        }
      }, 4000);
    };
    startAutoScroll();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [api, isPaused]);
  return <section id="client-testimonials" ref={ref} className="bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className={`mb-12 text-center transition-all duration-[var(--duration-medium)] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Client Testimonials
          </h2>
          <p className="mt-4 text-lg !text-[hsl(0,0%,100%)]">
            Hear from those who trust us with their most valuable assets
          </p>
        </div>

        <div className={`relative transition-all duration-[var(--duration-medium)] delay-100 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <Carousel setApi={setApi} opts={{
          align: "start",
          loop: true
        }} className="mx-auto w-full max-w-6xl">
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <Card className="h-full border border-border shadow-soft transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1 hover:shadow-medium" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
                    <CardContent className="flex h-full flex-col gap-4 p-6">
                      <SafeHydrate><Avatar className="size-12">
                        <AvatarFallback className="bg-primary text-foreground-primary text-sm font-semibold">
                          {testimonial.initials}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>

                      <blockquote className="flex-1 text-foreground">
                        <p className="leading-relaxed">
                          "{testimonial.quote}"
                        </p>
                      </blockquote>

                      {testimonial.name && <div className="mt-auto">
                          <div className="font-semibold text-foreground">
                            {testimonial.name}
                          </div>
                          {testimonial.company && <div className="text-sm !text-[hsl(0,0%,100%)]">
                              {testimonial.company}
                            </div>}
                        </div>}
                    </CardContent>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>

            <CarouselPrevious className="hover-lift -left-4 size-10 border-border bg-background text-foreground shadow-soft hover:bg-accent hover:text-foreground-accent focus-visible:ring-2 focus-visible:ring-primary/60 sm:-left-12">
              <ChevronLeft className="size-5" />
            </CarouselPrevious>

            <CarouselNext className="hover-lift -right-4 size-10 border-border bg-background text-foreground shadow-soft hover:bg-accent hover:text-foreground-accent focus-visible:ring-2 focus-visible:ring-primary/60 sm:-right-12">
              <ChevronRight className="size-5" />
            </CarouselNext>
          </Carousel>
        </div>
      </div>
    </section>;
}